import { faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, CardContent, Grid, IconButton, Tooltip as MuiTooltip, Skeleton, Stack, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/system';
import { parse as parseCsv } from 'json2csv';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import * as XLSX from 'xlsx';
import { objectIdToDate } from '../../../../../../utils/common';

interface TransformedDataPoint {
    date: string;
    countSubscribers: number;
    countTransitions: number;
}

interface ExportData {
    headers: { label: string; value: string }[];
    rows: TransformedDataPoint[];
}

const generateData = (
    data: {
        _id: string;
        payload: {
            countSubscribers: number;
            countTransitions: number;
        };
    }[],
) => {
    const result = {
        data: data.map(d => {
            return {
                time: objectIdToDate(d._id).valueOf(),
                countSubscribers: d.payload.countSubscribers,
                countTransitions: d.payload.countTransitions,
            };
        }),
    };

    return result;
};

/**
 * Transforms the counters data rows from the server to a format ready for export to CSV or Excel
 */
const transformCountersForExport = (
    data:
        | {
              _id: string;
              payload: {
                  countSubscribers: number;
                  countTransitions: number;
              };
          }[]
        | undefined,
    timezone: string,
): ExportData => {
    if (!data || data.length === 0) {
        return { headers: [], rows: [] };
    }

    const headers = [
        { label: `Date (${timezone})`, value: 'date' },
        { label: 'Subscribers', value: 'countSubscribers' },
        { label: 'Clicks', value: 'countTransitions' },
    ];

    const rows: TransformedDataPoint[] = data.map(d => ({
        date: moment(objectIdToDate(d._id)).tz(timezone).format('L'),
        countSubscribers: d.payload.countSubscribers,
        countTransitions: d.payload.countTransitions,
    }));

    return { headers, rows };
};

/**
 * Utility function to handle data export
 */
const exportData = (
    data:
        | {
              _id: string;
              payload: {
                  countSubscribers: number;
                  countTransitions: number;
              };
          }[]
        | undefined,
    metricId: string,
    timezone: string,
    format: 'csv' | 'excel',
): void => {
    const transformedData: ExportData = transformCountersForExport(data, timezone);
    const filename = `subscribersAndClicksByDate_${metricId}_${moment().tz(timezone, true).format('YYYY-MM-DD')}`;

    if (format === 'csv') {
        const csv = parseCsv(transformedData.rows, {
            fields: transformedData.headers,
            transforms: [],
        });
        const blob: Blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename}.csv`);
        document.body.appendChild(link);
        link.click();
        link.parentNode && link.parentNode.removeChild(link);
    } else if (format === 'excel') {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet([]);
        XLSX.utils.sheet_add_aoa(worksheet, [transformedData.headers.map(header => header.label)]);
        XLSX.utils.sheet_add_json(worksheet, transformedData.rows, {
            origin: 'A2',
            skipHeader: true,
            header: transformedData.headers.map(header => header.value),
        });
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Counters');
        XLSX.writeFile(workbook, `${filename}.xlsx`);
    }
};

const CustomTooltip = ({ active, payload, theme }: any) => {
    if (active && payload && payload.length) {
        return (
            <div>
                <Grid
                    container
                    flexGrow={0}
                    alignItems="center"
                    spacing={1}
                    sx={{
                        backgroundColor: '#fff',
                        borderRadius: 4,
                        border: '1px solid #999',
                        maxWidth: 200,
                    }}
                >
                    <Grid item xs={12}>
                        <span style={{ fontWeight: 'bold' }}>{moment(payload[0].payload.time).format('ddd Do, hh:mm A')}</span>
                    </Grid>
                    {payload.map((item: any, index: number) => (
                        <Grid item xs={12} key={index}>
                            {item.dataKey === 'countSubscribers' && (
                                <Grid container flexGrow={1} alignItems="center">
                                    <Grid item xs={4} sx={{ color: theme.palette.primary.main }}>
                                        Subscribers:
                                    </Grid>
                                    <Grid item xs={8} textAlign={'right'} sx={{ color: theme.palette.primary.main, paddingRight: '8px' }}>
                                        {item.value && typeof item.value === 'number' ? item.value.toLocaleString() : 0}
                                    </Grid>
                                </Grid>
                            )}

                            {item.dataKey === 'countTransitions' && (
                                <Grid container flexGrow={1} alignItems="center">
                                    <Grid item xs={4} sx={{ color: theme.palette.secondary.main }}>
                                        Clicks:
                                    </Grid>
                                    <Grid item xs={8} textAlign={'right'} sx={{ color: theme.palette.primary.main, paddingRight: '8px' }}>
                                        {item.value && typeof item.value === 'number' ? item.value.toLocaleString() : 0}
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    ))}
                </Grid>
            </div>
        );
    }

    return null;
};

type Props = {
    metricId: string;
    promoCampaignCounters:
        | {
              metricsDatas:
                  | {
                        _id: string;
                        payload: {
                            countSubscribers: number;
                            countTransitions: number;
                        };
                    }[]
                  | undefined;
          }
        | undefined;
    promoCampaignCountersLoading: boolean;
    timezone: string;
    theme: Theme;
};

const SextforceMetricsCampaignCountersGraph = (props: Props) => {
    const { metricId, promoCampaignCounters, promoCampaignCountersLoading, timezone, theme } = props;
    const [dataMain, setDataMain] = useState<{
        data: {
            time: number;
            countSubscribers: number;
            countTransitions: number;
        }[];
    }>({ data: [] });
    const largeScreen = useMediaQuery(theme.breakpoints.up('sm'));

    useEffect(() => {
        if (
            !promoCampaignCountersLoading &&
            promoCampaignCounters &&
            promoCampaignCounters.metricsDatas &&
            promoCampaignCounters.metricsDatas.length > 0
        ) {
            setDataMain(generateData(promoCampaignCounters.metricsDatas));
        } else {
            setDataMain({ data: [] });
        }
    }, [promoCampaignCounters, promoCampaignCountersLoading]);

    const handleDownloadReportCsv = () => {
        exportData(promoCampaignCounters?.metricsDatas, metricId, timezone, 'csv');
    };

    const handleDownloadReportExcel = () => {
        exportData(promoCampaignCounters?.metricsDatas, metricId, timezone, 'excel');
    };

    const Chart = (chartData: {
        data: {
            time: number;
            countSubscribers: number;
            countTransitions: number;
        }[];
    }) => (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart
                data={chartData && chartData.data ? chartData.data : []}
                margin={{
                    top: 0,
                    right: largeScreen ? -10 : -20,
                    left: largeScreen ? -10 : -20,
                    bottom: 60,
                }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="time"
                    type="number"
                    scale={'time'}
                    name="Time"
                    tickFormatter={value => (value ? moment(value).tz(timezone).format('L') : '')}
                    domain={chartData && chartData.data && chartData.data.length > 0 ? ['dataMin', 'dataMax'] : ['auto', 'auto']}
                    interval={'preserveStartEnd'}
                    tick={{ fontSize: 13 }}
                    angle={-90}
                    dy={35}
                    dx={-5}
                />
                <YAxis
                    yAxisId="countSubscribers"
                    dataKey={'countSubscribers'}
                    scale={'linear'}
                    domain={[(dataMin: number) => Math.abs(dataMin * 0.9), (dataMax: number) => Math.abs(dataMax * 1.1)]}
                    tickFormatter={(value: number) => value.toLocaleString()}
                    tick={{ fill: theme.palette.primary.main, fontSize: 13 }}
                />
                <Line
                    yAxisId="countSubscribers"
                    type="linear"
                    dataKey="countSubscribers"
                    stroke={theme.palette.primary.main}
                    activeDot={{ r: 0 }}
                    dot={{ r: 0 }}
                />
                <YAxis
                    yAxisId="countTransitions"
                    dataKey={'countTransitions'}
                    scale={'linear'}
                    name="Clicks Count"
                    domain={[0, (dataMax: number) => Math.abs(dataMax * 1.1)]}
                    tickFormatter={(value: number) => value.toLocaleString()}
                    orientation="right"
                    tick={{ fill: theme.palette.secondary.main, fontSize: 13 }}
                />
                <Line
                    yAxisId="countTransitions"
                    type="linear"
                    dataKey="countTransitions"
                    stroke={theme.palette.secondary.main}
                    activeDot={{ r: 0 }}
                    dot={{ r: 0 }}
                />
                <Tooltip content={<CustomTooltip payload={chartData.data} theme={theme} />} />
                <Legend
                    verticalAlign="top"
                    height={36}
                    formatter={(value: string) => (
                        <span style={{ fontSize: 13 }}>{value === 'countSubscribers' ? 'Subscribers Count' : 'Clicks Count'}</span>
                    )}
                />
            </LineChart>
        </ResponsiveContainer>
    );

    return (
        <Card sx={{ width: '100%', marginBottom: theme.spacing(2) }}>
            <CardContent>
                <Stack direction="column" spacing={1} sx={{ marginBottom: 1 }}>
                    <Stack direction="row" spacing={1} alignItems={'center'} justifyContent={'space-between'}>
                        <Typography variant="h5" sx={{ marginBottom: theme.spacing(1) }}>
                            Subscriptions Count and Clicks Count by Date
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems={'center'}>
                            <MuiTooltip title="Download as CSV">
                                <span>
                                    <IconButton
                                        disabled={promoCampaignCountersLoading}
                                        onClick={handleDownloadReportCsv}
                                        aria-label="Download as CSV"
                                    >
                                        <FontAwesomeIcon icon={faFileCsv} size="2x" color={theme.palette.info.main} />
                                    </IconButton>
                                </span>
                            </MuiTooltip>
                            <MuiTooltip title="Download as Excel">
                                <span>
                                    <IconButton
                                        disabled={promoCampaignCountersLoading}
                                        onClick={handleDownloadReportExcel}
                                        aria-label="Download as Excel"
                                    >
                                        <FontAwesomeIcon icon={faFileExcel} size="2x" color={theme.palette.info.main} />
                                    </IconButton>
                                </span>
                            </MuiTooltip>
                        </Stack>
                    </Stack>
                    <Typography variant="body1" sx={{ marginBottom: theme.spacing(1) }}>
                        The following graph shows how many fans subscribed and how many have just clicked on the promo, sorted by date since
                        the start of the campaign.
                    </Typography>
                </Stack>
                <Grid container flexGrow={1} spacing={0} sx={{ padding: 0 }}>
                    <Grid item xs={12} sx={{ height: { xs: 300, sm: 500 } }}>
                        {promoCampaignCountersLoading ? <Skeleton /> : Chart(dataMain)}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default SextforceMetricsCampaignCountersGraph;
